import {
    Dropdown,
    IDropdownOption,
    DropdownMenuItemType,
} from 'office-ui-fabric-react/lib/Dropdown';
import InboxRulesFolderDropdown from './FolderClauseValue';
import IncludesWordsInput from './IncludesWordsInput';
import getClauseTypeProperties from '../actions/helpers/getClauseTypeProperties';
import getExistingClauseTypesInSection from '../actions/helpers/getExistingClauseTypesInSection';
import getHeaderTextFromHeaderType from '../actions/helpers/getHeaderTextFromHeaderType';
import getImportanceOptions from '../actions/helpers/getImportanceOptions';
import getInboxRuleMessageFlagOptions from '../actions/helpers/getInboxRuleMessageFlagOptions';
import getInboxRuleMessageTypeOptions from '../actions/helpers/getInboxRuleMessageTypeOptions';
import getSensitivityOptions from '../actions/helpers/getSensitivityOptions';
import setClauseErrorText from '../actions/helpers/setClauseErrorText';
import setFocusedClauseId from '../actions/helpers/setFocusedClauseId';
import setIncludesWordsInputText from '../actions/helpers/setIncludesWordsInputText';
import removeClause from '../actions/removeClause';
import setClauseType from '../actions/setClauseType';
import setClauseValue from '../actions/setClauseValue';
import ClauseSectionType from '../actions/types/ClauseSectionType';
import ActionClauseType from '../store/schema/ActionClauseType';
import ClauseTypeType from '../store/schema/ClauseTypeType';
import ClauseValueDropdownData from '../store/schema/ClauseValueDropdownData';
import ConditionalExceptionClauseType from '../store/schema/ConditionalExceptionClauseType';
import InboxRuleClauseType from '../store/schema/InboxRuleClauseType';
import store from '../store/store';
import isValidClause from '../utils/isValidClause';
import { observer } from 'mobx-react';
import { IconButton } from 'office-ui-fabric-react/lib/Button';
import { TextField } from 'office-ui-fabric-react/lib/TextField';
import { ControlIcons } from 'owa-control-icons';
import { OwaDate } from 'owa-datetime';
import { userDate } from 'owa-datetime/lib/userDate';
import { DropdownDatePicker } from 'owa-datepicker';
import now from 'owa-datetime/lib/userDate/now';
import RecipientWellWithFindControlViewState, {
    ReadWriteRecipientWellSize,
} from 'owa-recipient-common/lib/store/schema/RecipientWellWithFindControlViewState';
import ReadWriteRecipientWellWithFindControl from 'owa-readwrite-recipient-well/lib/components/ReadWriteRecipientWellWithFindControl';
import { RecipientType } from 'owa-recipient-common/lib/store/schema/RecipientType';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import strings from 'owa-strings/owa-options-strings';
import * as React from 'react';
import CategoryClauseValue from 'owa-inbox-rules-option/lib/components/CategoryClauseValue';

const styles = require('./InboxRuleClause.scss');
let classNames = require('classnames/bind').bind(styles);

export interface InboxRuleClauseProps {
    clauseId: string;
    clauseTypeOptions: ActionClauseType[] | ConditionalExceptionClauseType[];
    sectionType: ClauseSectionType;
    sectionClauseIds: string[];
}

@observer
export default class InboxRuleClause extends React.Component<InboxRuleClauseProps, {}> {
    private recipientWellControl: ReadWriteRecipientWellWithFindControl;
    private includesWordsControl: IncludesWordsInput;
    private tryAutoFocus: boolean;

    componentDidUpdate() {
        // set focus on input if clause type requires text input
        if (this.tryAutoFocus) {
            let selectedClause = store.inboxRulesEditState.clauseMap.get(this.props.clauseId);

            switch (selectedClause.clauseType) {
                case 'from':
                case 'to':
                    window.requestAnimationFrame(() => {
                        this.recipientWellControl.focus();
                    });
                    break;
                case 'subjectIncludes':
                case 'subjectOrBodyIncludes':
                case 'inTheBody':
                case 'inTheSenderAddress':
                case 'inTheRecipientAddress':
                case 'inTheMessageHeader':
                    window.requestAnimationFrame(() => {
                        this.includesWordsControl.focus();
                    });
                    break;
                default:
                    // not a clauseType that should gain focus
                    break;
            }
            this.tryAutoFocus = false;
        }
    }

    render() {
        let clauseId = this.props.clauseId;
        let sectionType = this.props.sectionType;
        let selectedClause = store.inboxRulesEditState.clauseMap.get(clauseId);

        return (
            <div className={styles.clauseContainer} key={clauseId}>
                <Dropdown
                    onFocus={() => setFocusedClauseId(clauseId, true /* shouldSetAsFocused */)}
                    onBlur={() => setFocusedClauseId(clauseId, false /* shouldSetAsFocused */)}
                    id={'dropdown' + clauseId}
                    placeHolder={strings.options_inboxRules_clauseTypeDropdownPlaceholder}
                    options={this.getClauseDropdownOptions()}
                    selectedKey={this.getDropdownKey(selectedClause.clauseType)}
                    className={styles.clauseTypesDropdown}
                    onChanged={option => this.onClauseTypeChanged(option)}
                />
                {this.getClauseValueInput()}
                {this.shouldShowRemoveClauseButton() && (
                    <IconButton
                        className={styles.removeClauseButton}
                        iconProps={{
                            iconName: ControlIcons.Cancel,
                            styles: {
                                root: styles.cancelIcon,
                            },
                        }}
                        onClick={() => removeClause(clauseId, sectionType)}
                    />
                )}
            </div>
        );
    }

    private getClauseDropdownOptions(): IDropdownOption[] {
        let items: IDropdownOption[] = [];
        let clauseId = this.props.clauseId;
        let sectionType = this.props.sectionType;
        let clauseTypeOptions = this.props.clauseTypeOptions;
        let existingClauseTypes = getExistingClauseTypesInSection(this.props.sectionType);
        let selectedClause = store.inboxRulesEditState.clauseMap.get(clauseId);
        let previousHeaderType = null;

        for (let i = 0; i < clauseTypeOptions.length; i++) {
            let clauseType = clauseTypeOptions[i];
            let isSelectedClauseType = selectedClause.clauseType == clauseType;
            let hasClauseType = existingClauseTypes.has(clauseType);

            // Do not add the clause type to the dropdown options if the clause type...
            // 1) is not the current clause's type AND
            // 2) has been used already OR
            // 3) is in exception section and the clause type is any since that is not a valid choice
            if (
                (!isSelectedClauseType && hasClauseType) ||
                (clauseType === 'any' && sectionType === 'exception')
            ) {
                continue;
            }

            let clauseTypeProperties = getClauseTypeProperties(clauseType);
            let clauseHeaderType = clauseTypeProperties.headerType;

            // Only push a header if we are first element or if the next clause type's header is different than the previous'
            if (!previousHeaderType || clauseHeaderType != previousHeaderType) {
                items.push({
                    key: sectionType + clauseHeaderType,
                    text: getHeaderTextFromHeaderType(clauseHeaderType),
                    itemType: DropdownMenuItemType.Header,
                });
            }

            // If clause type is junk, need to make sure the segmentation settings have junk turned on before adding to dropdown
            if (clauseType !== 'junk' || getUserConfiguration().SegmentationSettings.JunkEMail) {
                items.push({
                    key: this.getDropdownKey(clauseType),
                    text: clauseTypeProperties.dropdownOptionText,
                    data: clauseTypeProperties,
                });
            }

            previousHeaderType = clauseHeaderType;
        }

        return items;
    }

    private getDropdownKey(clauseType: ClauseTypeType): string {
        return this.props.sectionType + clauseType;
    }

    private onClauseTypeChanged(clauseTypeDropdownOption: IDropdownOption) {
        let clauseId = this.props.clauseId;
        let clauseType = clauseTypeDropdownOption.data.clauseType;
        setClauseType(clauseType, clauseId);
        setClauseValue(clauseTypeDropdownOption.data.initialValue, clauseId);
        // clear the error text if the previous clause had invalid input
        setClauseErrorText(clauseId, true /* isValidClause */);
        this.tryAutoFocus = true;
    }

    private onClauseValueChanged(clauseValueDropdownOption: IDropdownOption) {
        let clauseId = this.props.clauseId;
        setClauseValue(clauseValueDropdownOption.data, clauseId);
        // clear the error text if the previous value was invalid
        setClauseErrorText(clauseId, true /* isValidClause */);
    }

    private getClauseValueInput = (): JSX.Element => {
        let clauseId = this.props.clauseId;
        let selectedClause = store.inboxRulesEditState.clauseMap.get(this.props.clauseId);

        switch (selectedClause.clauseType) {
            case 'from':
            case 'to':
            case 'forwardTo':
            case 'forwardAsAttachmentTo':
            case 'redirectTo':
                return this.renderRecipientWell(selectedClause);
            case 'subjectIncludes':
            case 'subjectOrBodyIncludes':
            case 'inTheBody':
            case 'inTheSenderAddress':
            case 'inTheRecipientAddress':
            case 'inTheMessageHeader':
                setIncludesWordsInputText(clauseId, '');
                return this.renderIncludesWordsInput(clauseId);
            case 'withImportance':
            case 'markImportance':
                return this.renderClauseValueDropdown(getImportanceOptions());
            case 'category':
                return <CategoryClauseValue clauseId={this.props.clauseId} />;
            case 'sensitivity':
                return this.renderClauseValueDropdown(getSensitivityOptions());
            case 'flag':
                return this.renderClauseValueDropdown(getInboxRuleMessageFlagOptions());
            case 'type':
                return this.renderClauseValueDropdown(getInboxRuleMessageTypeOptions());
            case 'atLeast':
            case 'atMost':
                return this.renderNumberTextField(selectedClause);
            case 'moveTo':
            case 'copyTo':
                return this.renderFolderDropdown(clauseId);
            case 'onTheToOrCcLine':
            case 'theOnlyRecipientListed':
            case 'onTheToLine':
            case 'onTheCcLine':
            case 'notOnTheToLine':
            case 'attachment':
            case 'delete':
            case 'pin':
            case 'read':
            case 'junk':
            case 'any':
            case null: // no clause selected from the clause type dropdown ('select a clause')
                return null; // No second input
            case 'before':
            case 'after':
                return this.renderDatePicker(selectedClause);
            // Other cases return null, default throw error
            default:
                throw new Error(
                    'Cannot get clause value input for a clause type that does not exist'
                );
        }
    };

    private renderIncludesWordsInput(clauseId: string) {
        let selectedClause = store.inboxRulesEditState.clauseMap.get(clauseId);
        return (
            <IncludesWordsInput
                ref={ref => (this.includesWordsControl = ref)}
                clauseId={clauseId}
                placeHolderText={this.getIncludesWordsPlaceholderText(selectedClause.clauseType)}
                onFocus={() => setFocusedClauseId(clauseId, true /* shouldSetAsFocused */)}
                onBlur={() => this.removeFocusAndValidate()}
            />
        );
    }

    private renderClauseValueDropdown(dropdownOptionsData: ClauseValueDropdownData[]): JSX.Element {
        let dropdownOptions: IDropdownOption[] = [];
        let currentSelectedValue = store.inboxRulesEditState.clauseMap.get(this.props.clauseId)
            .clauseValue;
        let selectedKey = currentSelectedValue ? currentSelectedValue.toString() : null;
        let clauseId = this.props.clauseId;

        dropdownOptionsData.forEach(option => {
            dropdownOptions.push({
                key: option.value.toString(),
                text: option.text,
                data: option.value,
            });
        });

        return (
            <div className={styles.clauseValueContainer}>
                <Dropdown
                    onFocus={() => setFocusedClauseId(clauseId, true /* shouldSetAsFocused */)}
                    onBlur={() => this.removeFocusAndValidate()}
                    options={dropdownOptions}
                    onChanged={option => this.onClauseValueChanged(option)}
                    errorMessage={store.inboxRulesEditState.clauseErrorTextMap.get(clauseId)}
                    placeHolder={strings.options_inboxRules_clauseValueDropdownPlaceholder}
                    selectedKey={selectedKey}
                />
            </div>
        );
    }

    private renderFolderDropdown(clauseId: string): JSX.Element {
        return <InboxRulesFolderDropdown clauseId={clauseId} />;
    }

    private renderRecipientWell(selectedClause: InboxRuleClauseType): JSX.Element {
        let clauseId = this.props.clauseId;
        let errorText = store.inboxRulesEditState.clauseErrorTextMap.get(this.props.clauseId);
        let value = store.inboxRulesEditState.clauseMap.get(this.props.clauseId)
            .clauseValue as RecipientWellWithFindControlViewState;
        let numPeople;
        const isFocused = store.inboxRulesEditState.focusedClauseId == clauseId;
        if (value && value.recipients.length) {
            numPeople = value.recipients.length;
        } else {
            numPeople = 0;
        }
        // we are in error state if there are no recipients selected and the find result is not showing (the user is not selecting a recipient)
        let shouldShowError = numPeople == 0 && !value.shouldShowFindResult;
        return (
            <div className={styles.recipientWellContainer}>
                <div
                    className={classNames(
                        styles.recipientWell,
                        errorText && shouldShowError && styles.hasError,
                        isFocused && styles.focusBorder
                    )}
                    tabIndex={-1}
                    onFocus={() => setFocusedClauseId(clauseId, true /* shouldSetAsFocused */)}
                    onBlur={() => this.removeFocusAndValidate()}>
                    <ReadWriteRecipientWellWithFindControl
                        ref={ref => (this.recipientWellControl = ref)}
                        recipientWell={
                            selectedClause.clauseValue as RecipientWellWithFindControlViewState
                        }
                        recipientType={RecipientType.None}
                        size={ReadWriteRecipientWellSize.Small}
                    />
                </div>
                {shouldShowError && <span className={styles.errorText}> {errorText} </span>}
            </div>
        );
    }

    private renderNumberTextField(selectedClause: InboxRuleClauseType): JSX.Element {
        let clauseId = this.props.clauseId;
        return (
            <TextField
                className={styles.clauseDateSizeValueContainer}
                onFocus={() => setFocusedClauseId(clauseId, true /* shouldSetAsFocused */)}
                onBlur={() => this.removeFocusAndValidate()}
                value={selectedClause.clauseValue.toString()}
                onChanged={this.onTextFieldChanged}
                errorMessage={store.inboxRulesEditState.clauseErrorTextMap.get(clauseId)}
                suffix={strings.options_inboxRules_kb}
            />
        );
    }

    private renderDatePicker(selectedClause: InboxRuleClauseType): JSX.Element {
        let selectedClauseValue = selectedClause.clauseValue as string;
        let todayDate = now();
        let defaultDate = selectedClauseValue ? userDate(selectedClauseValue) : todayDate;

        return (
            <DropdownDatePicker
                className={styles.clauseDateSizeValueContainer}
                value={defaultDate}
                onSelectDate={this.onDateSelected}
                minDate={todayDate}
            />
        );
    }

    private getIncludesWordsPlaceholderText(clauseType: ClauseTypeType) {
        return clauseType == 'inTheSenderAddress' || clauseType == 'inTheRecipientAddress'
            ? strings.options_inboxRules_addressIncludesOptionPlaceholder
            : strings.options_inboxRules_containsOptionPlaceholder;
    }

    private onTextFieldChanged = (value: string): void => {
        let num = Number(value);
        setClauseValue(num, this.props.clauseId);
    };

    private onDateSelected = (date: OwaDate) => {
        setClauseValue(date.toISOString(), this.props.clauseId);
    };

    // show remove clause button if there is more than one clause in conditional/action sections or for
    // any clause in the exception section
    private shouldShowRemoveClauseButton(): boolean {
        // always show for exceptions since it's an optional field
        return (
            this.props.sectionType == 'exception' ||
            // otherwise, show if there is more than one clause in conditional/action sections
            this.props.sectionClauseIds.length > 1
        );
    }

    private removeFocusAndValidate = () => {
        let clauseId = this.props.clauseId;
        setClauseErrorText(
            clauseId,
            isValidClause(clauseId),
            store.inboxRulesEditState.clauseMap.get(this.props.clauseId).clauseValue
        );
        setFocusedClauseId(clauseId, false /* shouldSetAsFocused */);
    };
}
